import React, { AriaAttributes, InputHTMLAttributes } from 'react';
import classnames from 'classnames';
import s from './RadioButton.scss';

/**
 * Contains the `checked` value of a radio button.
 *
 * Use this in the children of a headless {@link RadioButton} to react to the `checked` state.
 */
export const RadioButtonContext = React.createContext<boolean>(false);

interface BaseProps extends InputHTMLAttributes<HTMLInputElement> {
  // Requiring all these since HTMLInputElement doesn't
  name: string;
  value: string;
  children: React.ReactNode;
  checked: boolean;

  onChange: (
    e:
      | React.KeyboardEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLInputElement>
  ) => void;

  className?: string;

  // These are duplicated here because typescript doesn't like us trying to require only one of them via the type shenanigans below.
  hasAccessibleText?: boolean;
  'aria-label'?: AriaAttributes['aria-label'];
  'aria-labelledby'?: AriaAttributes['aria-labelledby'];
}

// Duplicate doc strings below for better dev experience
interface PropsWithAriaLabel extends BaseProps {
  /**
   * We require {@link hasAccessibleText}, {@link aria-label}, or {@link aria-labelledby} so that screen readers aren't left out in cases where the radio button spec
   * doesn't contain text.
   *
   * Use {@link aria-label} in cases where there is no visible text.
   */
  'aria-label': AriaAttributes['aria-label'];
}

interface PropsWithAriaLabelledBy extends BaseProps {
  /**
   * We require {@link hasAccessibleText}, {@link aria-label}, or {@link aria-labelledby} so that screen readers aren't left out in cases that the radio button spec
   * doesn't contain text.
   *
   * Use {@link aria-labelledby} in cases where another element contains text that describes this radio button.
   */
  'aria-labelledby': AriaAttributes['aria-labelledby'];
}

interface PropsWithHasAccessibleText extends BaseProps {
  /**
   * We require {@link hasAccessibleText}, {@link aria-label}, or {@link aria-labelledby} so that screen readers aren't left out in cases that the radio button spec
   * doesn't contain text.
   *
   * Only use {@link hasAccessibleText} if the output of `renderRadioButton` has actual text that a screen reader can see.
   */
  hasAccessibleText: boolean;
}

export type Props =
  | PropsWithAriaLabel
  | PropsWithAriaLabelledBy
  | PropsWithHasAccessibleText;

/**
 * Because Omit<T> doesn't preserve the type shenanigans above, we need to have a custom Omit-like utility for this component's
 * props
 */
export type PropsWithout<T extends string> =
  | Omit<PropsWithAriaLabel, T>
  | Omit<PropsWithAriaLabelledBy, T>
  | Omit<PropsWithHasAccessibleText, T>;

/**
 * A headless Radio button that you can use to implement your own Radio buttons. This component exists to take the guesswork and frustration out of making an accessible implementation.
 *
 * It does this via requiring one of the following props: `hasAccessibleText`, `aria-label`, or `aria-labelledby`. Each prop has a description of when you should use it, so go check it out!
 *
 * BUG:
 * ---
 * If this RadioButton is included in a scrollable area, the scroll position will jump to the start of the scrollable area.
 * This is due to how the root label's `display: contents; position: relative;` interacts with  the hidden `<input>` tag's `position: absolute`
 * Because `position: relative` refuses to work with `display: contents`, the hidden input gets positioned in unexpected places.
 *
 * To work around this, you need to pass a `display: grid/flex/etc` style through the `className` prop.
 *
 * I've chosen not to mess with the `display: contents` rule inside this component's code since being headless a core part of its API.
 * TODO: Figure out a way to keep `display: contents` or similar while keeping the hidden input nearby
 */
const RadioButton = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      checked,
      children,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      className,
      onChange,
      // Just yanking this off so that we don't pass it through to the DOM
      hasAccessibleText,
      ...rest
    },
    ref
  ) => {
    return (
      <RadioButtonContext.Provider value={checked}>
        <label
          className={classnames(s['root'], className)}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        >
          {children}
          <input
            ref={ref}
            type="radio"
            className={s['input']}
            onChange={onChange}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === 'Space') {
                onChange && onChange(e);
              }
            }}
            checked={checked}
            {...rest}
          />
        </label>
      </RadioButtonContext.Provider>
    );
  }
);
RadioButton.displayName = 'RadioButton';

export default RadioButton;
